/**
 * Intake — the client-owned intake, driven by the STAGE ENGINE + the catalog-
 * driven MODULE LIBRARY.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE OWNER'S DIRECTION (honoured here):
 *    • ONE module library, built once (components/IntakeModule.tsx). A service
 *      is a SELECTION of module keys, resolved LIVE from the catalog adapter
 *      (lib/intakeCatalog.ts) — NO per-service form, NO hardcoded service name.
 *    • The service NAME shown everywhere comes from `catalog.name(tier)`.
 *    • The stage list comes from `resolveStages(tier)`, which reads the
 *      catalog's `intakeStages(tier)`. Rename a service or change its module
 *      selection in the catalog → this form adapts with no code change.
 *
 *  CONSENT is the mandatory, always-on Stage 0 for EVERY service — the flow
 *  cannot advance until every required consent box is ticked (D7). Each stage
 *  persists on advance (resumable + partial-save). A submitted stage becomes
 *  read-only until the client explicitly chooses to edit it.
 *
 *  THE DATA CONTRACT IS UNCHANGED. REAL modules write the SAME `answers.*`
 *  shape via the existing `saveIntake` (answers MERGED). The stage engine adds
 *  sibling fields only: `consent`, `stages`, and `intake_extras` (scaffold
 *  module captures). `answers` is never touched by the engine.
 *
 *  TIER. Defaults to the live L2B intake (`L2B_R1`). A `?tier=<KEY>` param
 *  selects any other service (L1A, L1B, L2A…, ASTROLOGY, WELLNESS, the
 *  ancillaries) — proving one form serves every service. AUTH unchanged.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Box, Btn3, EmptyNote, StageRail } from '../components/ui';
import { useToast } from '../components/Toast';
import { ClientPicker } from '../components/ClientPicker';
import { ConsentStage } from '../components/ConsentStage';
import {
  IntakeModule,
  type IntakeModuleHandlers,
  type PropertyRegistryCtx,
} from '../components/IntakeModule';

/**
 * AD-036 (rev 2026-06-24) — module keys whose renderer hosts the UNIFIED
 * property-details box (one box; each slot is link-or-new). On these the staff
 * registry context is threaded into the module so the per-slot link control
 * renders INLINE at the top of each property slot — no separate "Registry
 * property" box anymore (owner ruling: collapse the two boxes into one).
 */
const PROPERTY_REGISTRY_MODULE_KEYS = new Set([
  'l1a_property_details',
  'l1b_property_details',
  // AD-036.2 (2026-06-24) — the L1C/L2C drawings stage captures the property too;
  // thread the same registry context so its property block hosts the unified
  // link-or-new control (DrawingsUploadModule renders PropertySlotLink at its top).
  'drawings_upload',
]);
import IntakeLinkSendBar from '../components/IntakeLinkSendBar';
import IntakeSummarySheet from '../components/IntakeSummarySheet';
import { getClient, listenProjects, type LiveProject } from '../lib/projectStore';
import { writeDrawingSetThrough, linkPropertyToClient } from '../lib/propertyStore';
import { buildConceptFromDrawings } from '../lib/drawingWriteThrough';
import {
  resolveFormsPicker,
  projectOptionLabel,
  type PickerProject,
} from '../lib/intakeFormsPicker';
import { CONSENT_VERSION, REQUIRED_CONSENT_KEYS } from '../lib/consentContent';
import { activeIntakeCatalog, serviceLabel, spineTierKey } from '../lib/intakeCatalog';
import { validateModules } from '../lib/intakeModules';
import {
  resolveStages,
  birthOnlyStages,
  firstIncompleteIndex,
  isStageComplete,
  stageProgress,
  buildConsentAcceptance,
  consentStatusFor,
  loadStageState,
  saveConsent,
  saveStageStatus,
  saveModuleExtras,
  type StageDef,
  type StagesMap,
  type ConsentAcceptance,
  type IntakeExtras,
} from '../lib/intakeStages';
import {
  L2B_R1_TIER,
  blankIntakeForm,
  newRowId,
  loadIntake,
  loadTargetPerson,
  personIdForRow,
  saveIntake,
  saveBirthOnlyPerson,
  saveBirthOnlyConsentCarry,
  savePropertyLink,
  type IntakeForm,
  type ConcernInput,
  type PersonInput,
} from '../lib/intake';
import {
  type IntakeScope,
  canonicalIntakePath,
  pathString,
} from '../lib/intakeScope';
import { BIRTH_ONLY_SERVICE } from '../lib/intakeLanding';

/**
 * Format the consent `accepted_at` for the staff status line. Client-side it is
 * a Firestore Timestamp (has `.toDate()`); a freshly-saved local acceptance has
 * a serverTimestamp sentinel (no date yet) or a string. Returns null when no
 * readable date is available (the status line then omits the "on <date>").
 */
function consentAcceptedAtText(
  acceptance: ConsentAcceptance | null | undefined,
): string | null {
  const at = (acceptance as { accepted_at?: unknown } | null | undefined)?.accepted_at;
  if (!at) return null;
  if (typeof at === 'string') return at;
  if (typeof at === 'object' && typeof (at as { toDate?: unknown }).toDate === 'function') {
    try {
      return (at as { toDate: () => Date }).toDate().toLocaleDateString();
    } catch {
      return null;
    }
  }
  return null;
}

export default function Intake() {
  const { clientId: authClientId, user, role } = useAuth();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();

  const isClientSession = !!authClientId;
  const [pickedClientId, setPickedClientId] = useState<string>(
    authClientId || params.get('client') || '',
  );
  const activeClientId = isClientSession ? authClientId! : pickedClientId;

  // ── tier (which service): default the live L2B intake; ?tier=<KEY> swaps. ──
  const tierParam = (params.get('tier') || '').trim();
  // ── M5-S5 birth_only: a TARGETED intake that captures ONLY one person's birth
  // details (DOB/TOB/POB) for a specific personId (?person=). It is NOT a report
  // tier — the form renders only Consent + the People stage, pre-targeted to the
  // person, and writes that one person record directly (saveBirthOnlyPerson). ──
  const birthOnly = tierParam.toLowerCase() === BIRTH_ONLY_SERVICE;
  const birthPersonId = (params.get('person') || '').trim();
  // A form is NORMALLY opened FROM a lead or project — which already knows the
  // SERVICE (tier) and the CLIENT, so it arrives with both params pre-set and
  // there is no picker. Opened standalone from the Forms nav (no context), staff
  // pick the client AND (service unknown) the service. (owner ruling 2026-06-16)
  const launchedWithContext = !!tierParam && !!(params.get('client') || authClientId);
  // The stage engine resolves a round-tier (e.g. L2B_R1); the catalog is keyed
  // by service key (L2B). Map a bare service key → its R1 resolver tier.
  const stageTier = birthOnly
    ? 'BIRTH_ONLY_R1'
    : tierParam
      ? /_R\d+$/i.test(tierParam)
        ? tierParam
        : `${spineTierKey(tierParam)}_R1`
      : L2B_R1_TIER;
  // The Firestore doc tier: the live L2B intake stays `L2B_R1` (locked path);
  // other services write to their own `{KEY}_R1` doc. A birth_only top-up uses
  // its own `BIRTH_ONLY_R1` sidecar doc (consent + stages) and writes the one
  // targeted person directly to the persons subcollection.
  const docTier = stageTier;

  // ── property scope (M5-S4): ?property=<P#####> links this intake to a
  // Property entity. The location/floor modules are then SOURCED from that
  // record (REPORT_ENGINE_SEAM resolvePropertySource), so the client is not
  // asked to re-enter them. We just persist the link; no answers pre-fill. ──
  const propertyParam = (params.get('property') || '').trim();
  // M5-S5: a project id, when the form was opened from a project. Needed for the
  // staff "Request birth details" action (generateIntakeLink requires a pid).
  // AD-030: it ALSO scopes the intake doc — when present, intake reads/writes are
  // project-scoped (projects/{pid}/intake/{tier}) with a backward-compatible read
  // fallback + a legacy client-doc write mirror; absent → client-scoped.
  const pidParam = (params.get('pid') || '').trim();

  // The intake scope this form reads/writes. Always carries the active client;
  // carries the project when ?pid= is present.
  const intakeScope: IntakeScope = useMemo(
    () => ({ clientId: activeClientId, projectId: pidParam || undefined }),
    [activeClientId, pidParam],
  );

  const serviceName = useMemo(
    () => (birthOnly ? 'Birth details' : serviceLabel(stageTier)),
    [stageTier, birthOnly],
  );
  // M5-S5 Flag #1: birth_only renders a SINGLE-stage flow — ONLY the People
  // (birth details) stage. CONSENT IS WAIVED for birth_only (owner-approved
  // 2026-06-20): a DOB top-up is requested for an EXISTING client who already
  // gave consent at primary intake, so re-gating is friction with no compliance
  // gain. Consent is carried forward implicitly via a `consent_source:
  // 'primary_intake'` marker stamped at the save site (saveBirthOnlyConsentCarry
  // below) — NO empty/broken consent doc is written, and no consent stage is
  // rendered. REPORT TIERS (L1A/L1B/L2B/L3A/L3B) keep the full consent stage via
  // resolveStages — they are NOT touched by this waiver.
  const stages: StageDef[] = useMemo(
    () => (birthOnly ? birthOnlyStages() : resolveStages(stageTier)),
    [stageTier, birthOnly],
  );

  // ── form (answers) + stage-engine sidecar (consent + stages + extras) ──
  const [form, setForm] = useState<IntakeForm>(blankIntakeForm());
  const [consent, setConsent] = useState<ConsentAcceptance | null>(null);
  const [consentFlags, setConsentFlags] = useState<Record<string, boolean>>({});
  const [stagesMap, setStagesMap] = useState<StagesMap>({});
  const [extras, setExtras] = useState<IntakeExtras>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Active stage index. Deep-link ?stage=<id> jumps if reachable; else resume.
  const [activeIndex, setActiveIndex] = useState(0);
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  // STAFF REVIEW: "Preview full form" — a read-only single-scroll render of ALL
  // stages + ALL fields, ignoring gating entirely, so the owner can read the
  // complete form at a glance (owner ruling 2026-06-16).
  const [previewAll, setPreviewAll] = useState(false);
  // M5-S5: staff "Request birth details" — the person we are minting a
  // birth_only link for (null = modal closed).
  const [birthReqPerson, setBirthReqPerson] = useState<{ id: string; name: string } | null>(null);
  // Once-guard so we write the property link at most once per (client,tier,property).
  const linkedPropRef = useRef('');
  // AD-040 — once-per-session guard so the drawing-set write-through does not
  // create a duplicate Concept when the drawings stage is saved AND submitted in
  // one session (keyed on a content signature of the drawing set + property).
  const writtenDrawingSig = useRef('');

  // AD-030 — live projects, for the standalone "Client OR Project" Forms picker.
  // Only needed for STAFF picking from the Forms nav (no project context);
  // skipped for client sessions + when launched with project context.
  const [allProjects, setAllProjects] = useState<LiveProject[]>([]);
  const needsPicker = !isClientSession && !launchedWithContext;
  useEffect(() => {
    if (!needsPicker) return;
    const unsub = listenProjects(setAllProjects, () => {});
    return unsub;
  }, [needsPicker]);
  // The picker's resolution for the chosen client: their projects + whether a
  // project choice is required (>1 project) + the single auto-scope (exactly 1).
  const pickerProjects: PickerProject[] = useMemo(
    () =>
      allProjects.map((p) => ({
        vp: p.vp,
        clientId: p.clientId,
        clientNo: p.clientNo,
        tier: p.tier,
        tierCode: p.tierCode,
      })),
    [allProjects],
  );
  const pickerRes = useMemo(
    () => resolveFormsPicker(pickerProjects, activeClientId),
    [pickerProjects, activeClientId],
  );
  // When the picked client has exactly ONE project, auto-scope the intake to it
  // (set ?pid=) so a single-project client never has to choose. Runs only in the
  // standalone-picker path; never overrides an explicit ?pid=.
  useEffect(() => {
    if (!needsPicker || !activeClientId || pidParam) return;
    if (pickerRes.autoProjectId) {
      const next = new URLSearchParams(params);
      next.set('pid', pickerRes.autoProjectId);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsPicker, activeClientId, pidParam, pickerRes.autoProjectId]);

  // Load all layers whenever the active client OR the tier changes.
  useEffect(() => {
    let alive = true;
    if (!activeClientId) {
      setForm(blankIntakeForm());
      setConsent(null);
      setConsentFlags({});
      setStagesMap({});
      setExtras({});
      setActiveIndex(0);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const [{ form: loaded }, sidecar] = await Promise.all([
          loadIntake({ clientId: activeClientId, projectId: pidParam || undefined }, docTier),
          loadStageState({ clientId: activeClientId, projectId: pidParam || undefined }, docTier),
        ]);
        if (!alive) return;
        let next = loaded;
        if (!next.client_name) {
          const c = await getClient(activeClientId).catch(() => null);
          if (c?.name) next = { ...next, client_name: c.name };
        }
        // M5-S5 birth_only: render exactly ONE person row, targeted at the link's
        // personId. Pre-seed name + any existing birth data from the person doc
        // so the client/staff only top up what is missing. Force astro_in_scope
        // so the birth fields are shown (the People module gates them on it).
        if (birthOnly && birthPersonId) {
          const existing = await loadTargetPerson(activeClientId, birthPersonId).catch(() => null);
          if (!alive) return;
          next = {
            ...next,
            astro_in_scope: true,
            persons: [
              {
                id: birthPersonId,
                name: existing?.name || next.client_name || '',
                role: existing?.relationship || '',
                dob: existing?.dob || '',
                time_of_birth: existing?.tob || '',
                place_of_birth: existing?.pob || '',
                lat: existing?.lat != null ? String(existing.lat) : '',
                lon: existing?.lon != null ? String(existing.lon) : '',
                tz: existing?.tz || '',
              } as PersonInput,
            ],
          };
        }
        if (!alive) return;
        setForm(next);
        setConsent(sidecar.consent);
        setConsentFlags(sidecar.consent?.flags || {});
        setStagesMap(sidecar.stages || {});
        setExtras(sidecar.extras || {});
        setEditing({});

        const resume = firstIncompleteIndex(
          stages,
          sidecar.stages,
          sidecar.consent,
          !isClientSession,
        );
        const wanted = params.get('stage');
        const wantedIdx = wanted ? stages.findIndex((s) => s.id === wanted) : -1;
        const start =
          wantedIdx >= 0 && wantedIdx <= resume
            ? wantedIdx
            : Math.min(resume, stages.length - 1);
        setActiveIndex(Math.max(0, start));
      } catch {
        if (alive) toast('Could not load this intake (check connection).');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClientId, docTier, pidParam]);

  // Link this intake to its Property entity when ?property= is present. Safe to
  // call repeatedly (merge write); the ref keeps it to one write per scope.
  useEffect(() => {
    if (!activeClientId || !propertyParam || loading) return;
    const key = `${activeClientId}|${pidParam}|${docTier}|${propertyParam}`;
    if (linkedPropRef.current === key) return;
    linkedPropRef.current = key;
    // Writes `answers.propertyId` (merge) → resolvePropertySource reads floors/
    // grid from properties/{propertyId}. No local form mutation needed.
    savePropertyLink({ clientId: activeClientId, projectId: pidParam || undefined }, docTier, propertyParam, null, user?.uid ?? 'unknown').catch(
      () => {
        // Non-fatal: the property can also be linked from the project later.
        linkedPropRef.current = '';
      },
    );
  }, [activeClientId, pidParam, docTier, propertyParam, loading, user]);

  function patch(p: Partial<IntakeForm>) {
    setForm((f) => ({ ...f, ...p }));
  }

  const actor = {
    uid: user?.uid ?? null,
    via: (isClientSession ? 'client' : 'staff') as 'client' | 'staff',
  };

  // Consent is the CLIENT's, not staff's (owner ruling 2026-06-16). When STAFF
  // fill on the client's behalf, consent is NEVER a gate — they see the client's
  // consent STATUS, never a sign-box, and never accept on the client's behalf.
  const viewerIsStaff = !isClientSession;

  const activeStage = stages[activeIndex];
  const progress = stageProgress(stages, activeIndex, stagesMap, consent, viewerIsStaff);

  /* ── row helpers (unchanged answers shape) ── */
  const handlers: IntakeModuleHandlers = {
    patch,
    addConcern: (text = '') =>
      patch({ presenting_concerns: [...form.presenting_concerns, { text, severity: '' }] }),
    setConcern: (i, c) => {
      const next = form.presenting_concerns.slice();
      next[i] = { ...next[i], ...c } as ConcernInput;
      patch({ presenting_concerns: next });
    },
    removeConcern: (i) =>
      patch({ presenting_concerns: form.presenting_concerns.filter((_, j) => j !== i) }),
    addGoal: () => patch({ goals: [...form.goals, ''] }),
    setGoal: (i, v) => {
      const next = form.goals.slice();
      next[i] = v;
      patch({ goals: next });
    },
    removeGoal: (i) => patch({ goals: form.goals.filter((_, j) => j !== i) }),
    addPerson: () =>
      patch({ persons: [...form.persons, { id: newRowId('p'), name: '', role: '' } as PersonInput] }),
    setPerson: (i, p) => {
      const next = form.persons.slice();
      next[i] = { ...next[i], ...p };
      patch({ persons: next });
    },
    removePerson: (i) => patch({ persons: form.persons.filter((_, j) => j !== i) }),
    getExtra: (moduleKey, name) => extras[moduleKey]?.[name] ?? '',
    setExtra: (moduleKey, name, value) =>
      setExtras((e) => ({ ...e, [moduleKey]: { ...(e[moduleKey] || {}), [name]: value } })),
  };

  /* ── consent stage handlers ── */
  function toggleConsent(key: string, value: boolean) {
    setConsentFlags((f) => ({ ...f, [key]: value }));
  }
  async function acceptConsent() {
    if (!activeClientId) {
      toast('Select a client first.');
      return;
    }
    const acceptance = buildConsentAcceptance(consentFlags, CONSENT_VERSION, actor.via);
    if (!acceptance.accepted) {
      toast('Please tick all required consent boxes.');
      return;
    }
    setSaving(true);
    try {
      await saveConsent(intakeScope, acceptance, actor, docTier);
      await saveStageStatus(intakeScope, 'consent', 'submitted', actor, docTier);
      setConsent(acceptance);
      setStagesMap((m) => ({ ...m, consent: { status: 'submitted' } }));
      setEditing((e) => ({ ...e, consent: false }));
      toast('Consent recorded.');
      goNext();
    } catch {
      toast('Save failed — your sign-in may not allow writing this client.');
    } finally {
      setSaving(false);
    }
  }

  /* ── staff-only: record that consent was obtained OFFLINE (audited note) ──
   * This is NOT a staff "acceptance" standing in for the client. It records,
   * with the actor + offline flag, that the client gave consent away from the
   * portal (e.g. on a call). The required flags are stored so the client's
   * status reads "obtained", but `accepted_via:'staff'` + `obtained_offline`
   * make the provenance explicit and audited. Staff-only path. */
  async function recordConsentOffline() {
    if (!activeClientId) {
      toast('Select a client first.');
      return;
    }
    const offlineFlags: Record<string, boolean> = {};
    for (const k of REQUIRED_CONSENT_KEYS) offlineFlags[k] = true;
    const acceptance: ConsentAcceptance = {
      ...buildConsentAcceptance(offlineFlags, CONSENT_VERSION, actor.via),
      obtained_offline: true,
    };
    setSaving(true);
    try {
      await saveConsent(intakeScope, acceptance, actor, docTier);
      await saveStageStatus(intakeScope, 'consent', 'submitted', actor, docTier);
      setConsent(acceptance);
      setStagesMap((m) => ({ ...m, consent: { status: 'submitted' } }));
      toast('Recorded: consent obtained offline (audited).');
    } catch {
      toast('Save failed — your sign-in may not allow writing this client.');
    } finally {
      setSaving(false);
    }
  }

  /* ── field-stage save: merge `answers` (real modules) + extras (scaffolds) ── */
  /**
   * AD-040 — POST-SAVE write-through. Runs after the intake answers are
   * persisted. STAFF-only, best-effort (never blocks the save), forward-only:
   * the drawing set → a NEW Design Concept on the linked property (Concept ▸
   * Version ▸ Floors + plan refs), tagged with provenance, + link the client to
   * the property (clientRefs[]). Idempotent per session via `writtenDrawingSig`.
   *
   * NOTE (BATCH 1, 2026-06-25): coordination CONTACTS (architect / interior
   * designer / consultants) no longer ride here — they moved OUT of the drawings
   * block into the Design brief's "Coordination · project team" section, where
   * each row links/creates a role-tagged Contact LIVE via ContactSlotLink. So this
   * hook is drawing-set-only now.
   */
  async function runDrawingWriteThrough(): Promise<void> {
    if (!viewerIsStaff) return; // clients never touch the registry
    const d = form.drawings;
    if (!d) return;
    const propertyId = (d.propertyId || '').trim();
    if (!propertyId) return;

    // Content signature: property + each version's stage/label + floor plan urls.
    const sig = JSON.stringify({
      p: propertyId,
      v: (d.versions || []).map((v) => ({
        s: v.stage,
        l: v.label,
        f: (v.floors || []).map((f) => `${f.floor}|${f.planUrl || ''}`),
      })),
    });
    if (sig === writtenDrawingSig.current) return;
    try {
      const concept = buildConceptFromDrawings(d, {
        sourceIntakeId: intakeScope.projectId || intakeScope.clientId,
        tier: docTier,
        actorUid: actor.uid ?? undefined,
      });
      if (concept) {
        const cid = await writeDrawingSetThrough(propertyId, concept);
        if (activeClientId) {
          await linkPropertyToClient(propertyId, activeClientId).catch(() => {});
        }
        writtenDrawingSig.current = sig;
        if (cid) {
          toast(`Drawing set saved to property ${propertyId} as a new design concept.`, { ok: true });
        }
      }
    } catch {
      // best-effort — the intake answers already saved; surface softly.
      toast(`Saved — could not write the drawing set into property ${propertyId} (try again from the Property page).`);
    }
  }

  async function saveFieldStage(stage: StageDef, advance: boolean) {
    if (!activeClientId) {
      toast('Select a client first.');
      return;
    }
    // Required-field enforcement: a HARD block only on the CLIENT self-fill
    // path (their submit must be complete). STAFF reviewing/filling on-behalf
    // get a SOFT, non-blocking warning and may partial-save a half-filled stage
    // and move on (owner ruling 2026-06-16). Required fields stay visibly
    // marked either way; staff are never hard-blocked.
    const errs = validateModules(stage.moduleKeys, stage.fieldFlags || {}, form);
    if (errs.length) {
      if (!viewerIsStaff) {
        toast(errs[0]);
        return;
      }
      toast(`Saved with gaps — still needed: ${errs[0]}`);
    }
    setSaving(true);
    try {
      // M5-S5 birth_only: write ONLY the one targeted person record (exact
      // personId from the link), NOT the full report answers doc. The astro CF
      // (AD-020) mirrors it to the engine. No scaffold extras, no answers merge.
      if (birthOnly && birthPersonId && form.persons[0]) {
        await saveBirthOnlyPerson(activeClientId, birthPersonId, form.persons[0], actor.via);
        // Flag #1: consent is WAIVED for birth_only; record the carry-forward
        // provenance (consent_source:'primary_intake') so there is no dangling
        // required consent and the waiver is audited. Non-fatal if it fails —
        // the person write (the actual top-up) already succeeded above.
        await saveBirthOnlyConsentCarry(intakeScope, actor, docTier).catch(() => {});
      } else {
        // Persist the shared answers form (merge-safe). Real modules read from it.
        await saveIntake(intakeScope, form, actor, docTier);
        // Persist each scaffold module's extras slot (one owner each, dotted path).
        for (const key of stage.moduleKeys) {
          const vals = extras[key];
          if (vals && Object.keys(vals).length) {
            await saveModuleExtras(intakeScope, key, vals, actor, docTier);
          }
        }
      }
      await saveStageStatus(intakeScope, stage.id, 'submitted', actor, docTier);
      // AD-040 — when this stage owns the drawings module, write the set into the
      // linked property + create coordination contacts (staff-only, best-effort).
      if (stage.moduleKeys.includes('drawings_upload')) {
        await runDrawingWriteThrough();
      }
      setStagesMap((m) => ({ ...m, [stage.id]: { status: 'submitted' } }));
      setEditing((e) => ({ ...e, [stage.id]: false }));
      toast('Saved.');
      if (advance) goNext();
    } catch {
      toast('Save failed — your sign-in may not allow writing this client.');
    } finally {
      setSaving(false);
    }
  }

  /* ── M5-S5 staff-only: "Request birth details" for person row i ──
   * Opens the send modal in birth_only mode, targeted at that person's stable
   * personId (row 0 → `${cid}_primary`, else the row id). Flag-#2 (AD-008/
   * AD-019): persons are CLIENT-scoped, not project-scoped — a birth_only link
   * needs only the client + personId. A `pid` is OPTIONAL context; when the form
   * was opened from a project we thread it through, otherwise we omit it. The
   * person should be saved first so its record exists, but the link still works
   * even if the doc is created on first birth submit. */
  function requestBirthDetails(i: number) {
    const p = form.persons[i];
    if (!p || !activeClientId) return;
    const personId = personIdForRow(activeClientId, i, p.id);
    setBirthReqPerson({ id: personId, name: (p.name || '').trim() || 'this person' });
  }

  function goNext() {
    setActiveIndex((i) => Math.min(i + 1, stages.length - 1));
  }
  // STAFF REVIEW (owner ruling 2026-06-16): staff can jump to ANY stage to
  // review what is coming, regardless of whether earlier stages are complete —
  // no stage is locked for staff navigation. The CLIENT self-fill path keeps
  // its guided order: a client can only reach up to the first incomplete stage.
  function canNavTo(i: number): boolean {
    if (i < 0 || i >= stages.length) return false;
    if (viewerIsStaff) return true;
    return i <= firstIncompleteIndex(stages, stagesMap, consent, viewerIsStaff);
  }
  function goTo(i: number) {
    if (canNavTo(i)) setActiveIndex(Math.max(0, Math.min(i, stages.length - 1)));
  }
  function editStage(id: string) {
    setEditing((e) => ({ ...e, [id]: true }));
  }

  const stageDone = (s: StageDef) =>
    isStageComplete(s, stagesMap[s.id], consent, viewerIsStaff);
  const stageLocked = (s: StageDef) => {
    // The consent stage, for staff, is always a read-only STATUS panel (they
    // never sign for the client) — never an "Edit"-able stage.
    if (s.consent && viewerIsStaff) return true;
    return stageDone(s) && !editing[s.id];
  };

  /* ── stage body: render the stage's selected modules via the library ── */
  function renderActiveStageBody() {
    if (!activeStage) return null;
    const locked = stageLocked(activeStage);

    if (activeStage.consent) {
      // STAFF-ON-BEHALF: consent is the client's. Staff see the client's consent
      // STATUS — never a sign-box. They are NOT asked to accept on the client's
      // behalf (we never write a staff "acceptance" as if it were the client's).
      if (viewerIsStaff) {
        const status = consentStatusFor(consent, consentAcceptedAtText(consent));
        return (
          <Box title="Consent & privacy" note="The client's consent — collected from the client, not staff">
            <p style={{ margin: '0 0 10px', fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>
              {status.summary}
            </p>
            <p style={{ margin: '0 0 4px', fontSize: 13, color: 'var(--ink-mute)', lineHeight: 1.55 }}>
              {status.obtained
                ? 'Consent is on file for this client. You can fill the rest of the form on their behalf below.'
                : 'You can fill this form on the client’s behalf now. Consent is the client’s own — it is collected when the client opens their own form/link, or you may record that it was obtained offline.'}
            </p>
            {!status.obtained && (
              <div style={{ marginTop: 10 }}>
                <Btn3 intent="ghost" size="sm" onClick={recordConsentOffline} disabled={saving}>
                  {saving ? 'Recording…' : 'Record: consent obtained offline'}
                </Btn3>
                <span style={{ marginLeft: 10, fontSize: 11.5, color: 'var(--ink-mute)' }}>
                  Audited note — does not stand in for the client ticking the boxes themselves.
                </span>
              </div>
            )}
          </Box>
        );
      }
      // CLIENT self-fill: consent now lives on the FINAL "Review & submit" tab
      // (owner ruling 2026-06-22). The boxes here only set the tick-state; the
      // page's "Save & Submit" button captures the acceptance and submits in one
      // action, so the in-component "I agree — continue" button is hidden.
      return (
        <ConsentStage
          flags={consentFlags}
          onToggle={toggleConsent}
          onAccept={acceptConsent}
          saving={saving}
          alreadyAccepted={!!consent?.accepted}
          readOnly={locked}
          hideAccept
        />
      );
    }
    // AD-036 (rev 2026-06-24) — staff registry context for the UNIFIED property
    // box. When staff fill on behalf of a known client, the per-slot link-or-new
    // control renders INLINE inside the Property-details box (no separate box).
    // Absent for client self-fill (clients never touch the registry).
    const registryActive = viewerIsStaff && !!activeClientId;
    const propertyRegistry: PropertyRegistryCtx | undefined = registryActive
      ? {
          scope: intakeScope,
          tier: docTier,
          actorUid: user?.uid ?? 'unknown',
          onMsg: (m, ok) => toast(m, ok ? { ok: true } : undefined),
        }
      : undefined;
    return (
      <>
        {activeStage.moduleKeys.map((key) => (
          <IntakeModule
            key={key}
            moduleKey={key}
            form={form}
            readOnly={locked}
            handlers={handlers}
            viewerIsStaff={viewerIsStaff}
            onRequestBirth={viewerIsStaff ? requestBirthDetails : undefined}
            serviceTier={stageTier}
            propertyRegistry={
              PROPERTY_REGISTRY_MODULE_KEYS.has(key) ? propertyRegistry : undefined
            }
          />
        ))}
      </>
    );
  }

  /* ── "Review & print" — the ONE complete summary sheet ──
   * Reads the WHOLE form (not just a tier's modules), so it always surfaces
   * email, WhatsApp, uploaded plans + media, and per-person astrology — plus a
   * "still needed" list. Opened by the "Review & print summary" button on the
   * final "Review & submit" tab. View + Print here; the actual Save & Submit
   * lives on the final tab next to the consent boxes (owner ruling 2026-06-22). */
  function renderReviewSheet() {
    return (
      <div data-testid="intake-preview-all">
        <div
          className="iss-no-print"
          style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '0 0 12px' }}
        >
          <Btn3 intent="ink" onClick={() => window.print()} disabled={saving}>
            🖨 Print / Save as PDF
          </Btn3>
          <Btn3 intent="ghost" onClick={() => setPreviewAll(false)} disabled={saving}>
            ← Back to the form
          </Btn3>
        </div>
        <IntakeSummarySheet
          serviceLabel={serviceName}
          clientId={activeClientId}
          docTier={docTier}
          form={form}
          missing={missing}
          stagesDone={stagesDone}
          stagesTotal={stages.length}
          consentOnFile={!!consent?.accepted}
        />
      </div>
    );
  }

  const isLastStage = activeIndex === stages.length - 1;
  const allDone =
    firstIncompleteIndex(stages, stagesMap, consent, viewerIsStaff) >= stages.length;

  // ── Summary / completeness (for the Review & print sheet, owner ask
  // 2026-06-22). `missing` aggregates the mandatory-but-empty items across every
  // field stage (via the same validateModules the per-stage save uses), de-duped.
  // Consent is added when it is not yet on file (it gates the client submit). ──
  const fieldStages = useMemo(() => stages.filter((s) => !s.consent), [stages]);
  const missing = useMemo(() => {
    const out: string[] = [];
    for (const s of fieldStages) {
      out.push(...validateModules(s.moduleKeys, s.fieldFlags || {}, form));
    }
    if (!consent?.accepted) out.push('Consent is not yet recorded.');
    return Array.from(new Set(out));
  }, [fieldStages, form, consent]);
  const stagesDone = useMemo(
    () => stages.filter((s) => stageDone(s)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stages, stagesMap, consent, viewerIsStaff],
  );
  // Are all REQUIRED consent boxes ticked? Gates the client's Save & Submit on
  // the final tab (owner ruling 2026-06-22). Staff never tick the client's
  // consent, so this gate never applies to them.
  const requiredConsentTicked = useMemo(
    () => REQUIRED_CONSENT_KEYS.every((k) => consentFlags[k] === true),
    [consentFlags],
  );

  /* ── Save & Submit (final "Review & submit" tab): for the CLIENT, capture the
   * ticked consent first (gated on the required boxes), then persist the WHOLE
   * form + extras and mark every stage submitted. STAFF never sign the client's
   * consent and may submit with gaps (soft note). ── */
  async function submitAll() {
    if (!activeClientId) {
      toast('Select a client first.');
      return;
    }
    // CLIENT: consent is captured here, as part of submit. Gate on the required
    // boxes; then hard-block on any remaining mandatory field gap.
    if (!viewerIsStaff) {
      if (!requiredConsentTicked) {
        toast('Please tick all required consent boxes to submit.');
        return;
      }
      const fieldMissing: string[] = [];
      for (const s of fieldStages) {
        fieldMissing.push(...validateModules(s.moduleKeys, s.fieldFlags || {}, form));
      }
      if (fieldMissing.length) {
        toast(`Still needed: ${fieldMissing[0]}`);
        return;
      }
    }
    setSaving(true);
    try {
      if (!viewerIsStaff) {
        const acceptance = buildConsentAcceptance(consentFlags, CONSENT_VERSION, actor.via);
        await saveConsent(intakeScope, acceptance, actor, docTier);
        await saveStageStatus(intakeScope, 'consent', 'submitted', actor, docTier);
        setConsent(acceptance);
        setStagesMap((m) => ({ ...m, consent: { status: 'submitted' } }));
      }
      await saveIntake(intakeScope, form, actor, docTier);
      for (const s of fieldStages) {
        for (const key of s.moduleKeys) {
          const vals = extras[key];
          if (vals && Object.keys(vals).length) {
            await saveModuleExtras(intakeScope, key, vals, actor, docTier);
          }
        }
        await saveStageStatus(intakeScope, s.id, 'submitted', actor, docTier);
      }
      // AD-040 — on full submit, write the drawing set into the linked property +
      // create coordination contacts (staff-only, idempotent per session).
      await runDrawingWriteThrough();
      setStagesMap((m) => {
        const next = { ...m };
        for (const s of fieldStages) next[s.id] = { status: 'submitted' };
        return next;
      });
      setEditing({});
      setPreviewAll(false);
      toast(missing.length && viewerIsStaff ? 'Saved with gaps (recorded).' : 'Submitted — all saved.');
    } catch {
      toast('Save failed — your sign-in may not allow writing this client.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '0 0 60px' }}>
      {/* M5-S5: staff "Request birth details" → birth_only intake link modal.
          Flag-#2: pid is OPTIONAL — pass it as context only when the form was
          opened from a project; the birth_only link works without it. */}
      {birthReqPerson && activeClientId && (
        <IntakeLinkSendBar
          cid={activeClientId}
          pid={pidParam || undefined}
          birthPersonId={birthReqPerson.id}
          birthPersonName={birthReqPerson.name}
          onClose={() => setBirthReqPerson(null)}
        />
      )}
      <div className="phead">
        <h1 style={{ margin: 0, fontSize: 20 }}>{serviceName} — Intake Form</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--ink-mute)', fontSize: 13 }}>
          Tell us about the property and the people who live or work there. Fields marked{' '}
          <em>optional</em> can be left blank.
        </p>
      </div>

      {!isClientSession && (
        <Box
          title="Client"
          note={launchedWithContext ? 'Opened from a lead / project' : 'Whose intake is this?'}
          allowOverflow
        >
          {!launchedWithContext && (
            <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--ink-mute)', lineHeight: 1.5 }}>
              Usually you’ll open a form from a lead or project — the service and the
              client come across automatically. Opened here on its own, pick the client
              and the service below.
            </p>
          )}
          <div style={{ marginBottom: 8 }}>
            <ClientPicker
              value={pickedClientId}
              onPick={(c) => {
                setPickedClientId(c.clientId);
                const next = new URLSearchParams(params);
                next.set('client', c.clientId);
                // A new client → a different project set. Clear any prior pid so
                // the per-client auto-scope / required-choice logic re-runs.
                next.delete('pid');
                setParams(next, { replace: true });
              }}
              ariaLabel="Select the client this intake is for"
            />
          </div>

          {/* AD-030 — Project picker: when the chosen client has projects, the
              intake is filed UNDER a project. One project auto-scopes; >1 REQUIRES
              a choice (a project-scoped intake must never land on the wrong
              project); 0 projects → client-scoped (universal Scoping path). */}
          {activeClientId && pickerRes.projects.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <label
                style={{ display: 'block', fontSize: 12, color: 'var(--ink-mute)', marginBottom: 4 }}
              >
                Project{pickerRes.needsChoice ? ' (choose one — this client has several)' : ''}
              </label>
              <select
                value={pidParam}
                onChange={(e) => {
                  const next = new URLSearchParams(params);
                  const v = e.target.value;
                  if (v) next.set('pid', v);
                  else next.delete('pid');
                  setParams(next, { replace: true });
                }}
                aria-label="Select which project this intake belongs to"
                style={{
                  border: `1px solid ${pickerRes.needsChoice && !pidParam ? 'var(--triage, #DC2626)' : 'var(--rule)'}`,
                  borderRadius: 'var(--r-sm)',
                  padding: '8px 10px',
                  fontSize: 13.5,
                  fontFamily: 'inherit',
                  color: 'var(--ink)',
                  background: 'var(--surface)',
                  minWidth: 280,
                }}
              >
                {pickerRes.needsChoice && <option value="">— choose a project —</option>}
                {pickerRes.projects.map((p) => (
                  <option key={p.vp} value={p.vp}>
                    {projectOptionLabel(p)}
                  </option>
                ))}
              </select>
              {pickerRes.needsChoice && !pidParam && (
                <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--triage, #DC2626)' }}>
                  This client has several projects — pick the one this intake is for.
                </p>
              )}
            </div>
          )}

          {/* Standalone service picker — only when there is no lead/project
              context to carry the service across. */}
          {!launchedWithContext && (
            <div style={{ marginBottom: 8 }}>
              <label
                style={{ display: 'block', fontSize: 12, color: 'var(--ink-mute)', marginBottom: 4 }}
              >
                Service / form
              </label>
              <select
                value={spineTierKey(tierParam || stageTier)}
                onChange={(e) => {
                  const next = new URLSearchParams(params);
                  next.set('tier', e.target.value);
                  setParams(next, { replace: true });
                }}
                aria-label="Select which service form to fill"
                style={{
                  border: '1px solid var(--rule)',
                  borderRadius: 'var(--r-sm)',
                  padding: '8px 10px',
                  fontSize: 13.5,
                  fontFamily: 'inherit',
                  color: 'var(--ink)',
                  background: 'var(--surface)',
                  minWidth: 280,
                }}
              >
                {activeIntakeCatalog
                  .tiers()
                  // SCOPING ("Consultation Scoping") is parked until the Leads
                  // rework (AD-033) — hide it from the live picker so staff don't
                  // file scoping on a client. Still reachable via ?tier=SCOPING.
                  .filter((t) => spineTierKey(t) !== 'SCOPING')
                  .map((t) => {
                    const key = spineTierKey(t);
                    return (
                      <option key={key} value={key}>
                        {serviceLabel(t)}
                      </option>
                    );
                  })}
              </select>
            </div>
          )}

          {activeClientId && (
            <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
              Writing to{' '}
              <code>{pathString(canonicalIntakePath(intakeScope, docTier))}</code>
              {pidParam && (
                <span style={{ marginLeft: 6, color: 'var(--ink-faint, var(--ink-mute))' }}>
                  (also mirrored to the client record while reports migrate)
                </span>
              )}
            </div>
          )}
        </Box>
      )}

      {!activeClientId && !isClientSession && (
        <EmptyNote title="Pick a client to begin">
          Choose a client above to start or resume their intake.
        </EmptyNote>
      )}

      {loading && <p style={{ color: 'var(--ink-mute)', fontSize: 13 }}>Loading intake…</p>}

      {/* AD-030 — Client → Project → Property scope banner. Makes it unambiguous
          which project this intake belongs to. Shown whenever a client is set;
          the Project crumb appears only when the form is project-scoped (?pid=). */}
      {activeClientId && !loading && !birthOnly && (
        <div
          data-testid="intake-scope-banner"
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 6,
            fontSize: 12.5,
            color: 'var(--ink-mute)',
            border: '1px solid var(--rule)',
            background: 'var(--surface)',
            borderRadius: 'var(--r-sm)',
            padding: '8px 12px',
            margin: '0 0 12px',
          }}
        >
          <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{activeClientId}</span>
          {pidParam ? (
            <>
              <span aria-hidden>→</span>
              <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{pidParam}</span>
            </>
          ) : (
            <span style={{ fontStyle: 'italic' }}>
              · no project linked (this intake is filed on the client until a project is created)
            </span>
          )}
          {propertyParam && (
            <>
              <span aria-hidden>→</span>
              <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{propertyParam}</span>
            </>
          )}
        </div>
      )}

      {/* M5-S4 — property-scope banner. The location & floor details come from
          the linked Property record, so they aren't asked again here. */}
      {propertyParam && activeClientId && !loading && (
        <div
          style={{
            border: '1px solid var(--accent)',
            background: 'var(--accent-wash)',
            color: 'var(--accent-deep, var(--ink))',
            borderRadius: 'var(--r-sm)',
            padding: '10px 12px',
            fontSize: 13,
            margin: '0 0 12px',
          }}
        >
          This intake is for property <strong>{propertyParam}</strong>. Its
          location and floor details are already on file — you won’t be asked to
          re-enter them.
        </div>
      )}

      {/* REVIEW & PRINT overlay — opened by the "Review & print summary" button
          on the final "Review & submit" tab (no longer a persistent top toggle,
          owner ruling 2026-06-22). View + Print here; submit on the final tab. */}
      {activeClientId && !loading && previewAll && renderReviewSheet()}

      {activeClientId && !loading && !previewAll && (
        <>
          {/* ── Progress rail (Stage N of M) ── */}
          <div style={{ margin: '4px 0 14px' }}>
            <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginBottom: 6 }}>
              Stage {progress.current + 1} of {progress.total}
              {allDone && ' · all stages complete'}
            </div>
            <div
              role="navigation"
              aria-label="Intake stages"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}
            >
              {stages.map((s, i) => {
                const clickable = canNavTo(i);
                return (
                  <button
                    key={s.id}
                    onClick={() => clickable && goTo(i)}
                    disabled={!clickable}
                    title={s.title}
                    style={{
                      cursor: clickable ? 'pointer' : 'default',
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      opacity: clickable ? 1 : 0.5,
                    }}
                  >
                    <StageRail stages={[{ label: s.title, state: progress.rail[i].state }]} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Edit affordance when a submitted stage is being viewed. NOT for the
              staff consent status panel — staff never edit the client's consent. */}
          {activeStage && !(activeStage.consent && viewerIsStaff) &&
            stageDone(activeStage) && !editing[activeStage.id] && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '0 0 8px',
                fontSize: 12,
                color: 'var(--ink-mute)',
              }}
            >
              <span>This stage is complete and read-only.</span>
              <Btn3 intent="ghost" size="sm" onClick={() => editStage(activeStage.id)}>
                Edit
              </Btn3>
            </div>
          )}

          {renderActiveStageBody()}

          {/* ── Nav bar ── */}
          <div
            style={{
              position: 'sticky',
              bottom: 0,
              background: 'var(--surface)',
              borderTop: '1px solid var(--rule)',
              padding: '12px 0',
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {activeIndex > 0 && (
              <Btn3 intent="ghost" onClick={() => goTo(activeIndex - 1)} disabled={saving}>
                ← Back
              </Btn3>
            )}

            {/* FINAL "Review & submit" tab (consent is last): Review & Print
                Summary + Save & Submit. For the client, Save & Submit is gated
                on the required consent boxes; staff submit on the client's
                behalf (their consent is captured/recorded separately). */}
            {activeStage && activeStage.consent && (
              <>
                <Btn3 intent="ghost" onClick={() => setPreviewAll(true)} disabled={saving}>
                  Review & print summary
                </Btn3>
                <Btn3
                  intent="grn"
                  onClick={submitAll}
                  disabled={saving || (!viewerIsStaff && !requiredConsentTicked)}
                  title={
                    !viewerIsStaff && !requiredConsentTicked
                      ? 'Tick the consent & privacy boxes to enable submit'
                      : undefined
                  }
                >
                  {saving ? 'Saving…' : 'Save & submit'}
                </Btn3>
              </>
            )}

            {activeStage && !activeStage.consent && !stageLocked(activeStage) && (
              <>
                <Btn3
                  intent="grn"
                  onClick={() => saveFieldStage(activeStage, !isLastStage)}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : isLastStage ? 'Save & finish' : 'Save & continue'}
                </Btn3>
                {!isLastStage && (
                  <Btn3
                    intent="ink"
                    size="sm"
                    onClick={() => saveFieldStage(activeStage, false)}
                    disabled={saving}
                  >
                    Save & stay
                  </Btn3>
                )}
              </>
            )}

            {activeStage && !activeStage.consent && stageLocked(activeStage) && !isLastStage && (
              <Btn3 intent="ink" onClick={goNext} disabled={saving}>
                Continue →
              </Btn3>
            )}

            {role && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-mute)' }}>
                {isClientSession ? 'Signed in as client' : 'Filling as team'}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
